import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { companyApi, invoicesApi, clientsApi } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FileText, Users, DollarSign, TrendingUp, ArrowRight, Building2, Receipt, FileCheck } from "lucide-react";
import OnboardingWizard from "@/components/OnboardingWizard";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CompanyDashboard = () => {
  const { user } = useAuth();
  const [company, setCompany] = useState(null);
  const [stats, setStats] = useState({
    totalInvoices: 0,
    totalClients: 0,
    totalRevenue: 0,
    monthlyInvoices: 0,
    totalCreditNotes: 0,
    totalCreditNotesAmount: 0,
    totalPaymentComplements: 0,
    totalPaymentsAmount: 0
  });
  const [recentInvoices, setRecentInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasCompany, setHasCompany] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const token = localStorage.getItem("cia_token");
      
      // Try to get company info
      try {
        const companyRes = await companyApi.getMyCompany();
        setCompany(companyRes.data);
        setHasCompany(true);
        
        // Load all data
        const [invoicesRes, clientsRes, creditNotesRes, paymentComplementsRes] = await Promise.all([
          invoicesApi.list(),
          clientsApi.list(),
          axios.get(`${API}/credit-notes`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/payment-complements`, { headers: { Authorization: `Bearer ${token}` } })
        ]);
        
        const invoices = invoicesRes.data;
        const clients = clientsRes.data;
        const creditNotes = creditNotesRes.data || [];
        const paymentComplements = paymentComplementsRes.data || [];
        
        // Calculate stats
        const totalRevenue = invoices
          .filter(inv => inv.status === "issued")
          .reduce((sum, inv) => sum + inv.total, 0);
        
        const thisMonth = new Date().toISOString().slice(0, 7);
        const monthlyInvoices = invoices.filter(inv => 
          inv.created_at?.startsWith(thisMonth)
        ).length;
        
        const totalCreditNotesAmount = creditNotes.reduce((sum, cn) => sum + (cn.total || 0), 0);
        const totalPaymentsAmount = paymentComplements.reduce((sum, pc) => sum + (pc.amount || 0), 0);
        
        setStats({
          totalInvoices: invoices.length,
          totalClients: clients.length,
          totalRevenue,
          monthlyInvoices,
          totalCreditNotes: creditNotes.length,
          totalCreditNotesAmount,
          totalPaymentComplements: paymentComplements.length,
          totalPaymentsAmount
        });
        
        setRecentInvoices(invoices.slice(0, 5));
      } catch (err) {
        if (err.response?.status === 404) {
          setHasCompany(false);
        }
      }
    } catch (error) {
      console.error("Error loading dashboard:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '-';
      return date.toLocaleDateString('es-MX');
    } catch {
      return '-';
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft: "badge-draft",
      issued: "badge-issued",
      cancelled: "badge-cancelled",
      cancellation_pending: "bg-orange-100 text-orange-800 px-2 py-1 rounded-full text-xs font-medium"
    };
    const labels = {
      draft: "Borrador",
      issued: "Timbrada",
      cancelled: "Cancelada",
      cancellation_pending: "Cancelación pendiente"
    };
    return <span className={badges[status] || badges.draft}>{labels[status] || status}</span>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5B9BD5]"></div>
      </div>
    );
  }

  if (!hasCompany) {
    return (
      <div className="max-w-2xl mx-auto" data-testid="no-company-view">
        <Card className="cia-card">
          <CardContent className="p-12 text-center">
            <div className="w-20 h-20 rounded-full bg-[#5B9BD5]/10 flex items-center justify-center mx-auto mb-6">
              <Building2 className="w-10 h-10 text-[#5B9BD5]" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">
              ¡Bienvenido, {user?.name}!
            </h2>
            <p className="text-slate-600 mb-8">
              Para comenzar a facturar, primero necesitas registrar los datos de tu empresa.
            </p>
            <Link to="/dashboard/settings">
              <button className="cia-btn-primary px-8 py-3" data-testid="setup-company-btn">
                <Building2 className="w-5 h-5 mr-2 inline" />
                Configurar Empresa
              </button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="company-dashboard">
      {/* Onboarding Wizard */}
      {showOnboarding && (
        <OnboardingWizard 
          onComplete={() => {
            setShowOnboarding(false);
            loadDashboardData();
          }}
          onSkip={() => setShowOnboarding(false)}
        />
      )}
      
      {/* Welcome header - Sin botón de nueva factura */}
      <div>
        <h1 className="page-title">
          ¡Hola, {user?.name}!
        </h1>
        <p className="page-subtitle">{company?.name} • RFC: {company?.rfc}</p>
      </div>

      {/* Stats cards - Ahora incluye NC y Complementos */}
      <div className="stats-grid">
        <Card className="metric-card">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
              <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
            </div>
            <div className="min-w-0">
              <p className="metric-label">Facturas</p>
              <p className="metric-value">{stats.totalInvoices}</p>
              <p className="text-xs text-slate-500 truncate">{formatCurrency(stats.totalRevenue)}</p>
            </div>
          </div>
        </Card>

        <Card className="metric-card">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-red-100 flex items-center justify-center flex-shrink-0">
              <Receipt className="w-5 h-5 sm:w-6 sm:h-6 text-red-600" />
            </div>
            <div className="min-w-0">
              <p className="metric-label">Notas de Crédito</p>
              <p className="metric-value">{stats.totalCreditNotes}</p>
              <p className="text-xs text-slate-500 truncate">-{formatCurrency(stats.totalCreditNotesAmount)}</p>
            </div>
          </div>
        </Card>

        <Card className="metric-card">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-green-100 flex items-center justify-center flex-shrink-0">
              <FileCheck className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
            </div>
            <div className="min-w-0">
              <p className="metric-label">Complementos Pago</p>
              <p className="metric-value">{stats.totalPaymentComplements}</p>
              <p className="text-xs text-slate-500 truncate">{formatCurrency(stats.totalPaymentsAmount)}</p>
            </div>
          </div>
        </Card>

        <Card className="metric-card">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-purple-100 flex items-center justify-center flex-shrink-0">
              <Users className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
            </div>
            <div className="min-w-0">
              <p className="metric-label">Clientes</p>
              <p className="metric-value">{stats.totalClients}</p>
              <p className="text-xs text-slate-500 truncate">Este mes: {stats.monthlyInvoices} facturas</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Resumen financiero */}
      <Card className="cia-card">
        <CardHeader>
          <CardTitle className="text-base sm:text-lg font-semibold text-slate-900">Resumen Financiero</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-6">
            <div className="p-3 sm:p-4 bg-green-50 rounded-lg">
              <p className="text-xs sm:text-sm text-green-700">Ingresos (Facturas)</p>
              <p className="text-lg sm:text-2xl font-bold text-green-800">{formatCurrency(stats.totalRevenue)}</p>
            </div>
            <div className="p-3 sm:p-4 bg-red-50 rounded-lg">
              <p className="text-xs sm:text-sm text-red-700">Egresos (NC)</p>
              <p className="text-lg sm:text-2xl font-bold text-red-800">-{formatCurrency(stats.totalCreditNotesAmount)}</p>
            </div>
            <div className="p-3 sm:p-4 bg-blue-50 rounded-lg">
              <p className="text-xs sm:text-sm text-blue-700">Ingresos Netos</p>
              <p className="text-lg sm:text-2xl font-bold text-blue-800">
                {formatCurrency(stats.totalRevenue - stats.totalCreditNotesAmount)}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Recent invoices */}
      <Card className="cia-card">
        <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <CardTitle className="text-base sm:text-lg font-semibold text-slate-900">Facturas Recientes</CardTitle>
          <Link to="/dashboard/invoices" className="text-[#5B9BD5] text-sm font-medium hover:underline flex items-center gap-1">
            Ver todas <ArrowRight className="w-4 h-4" />
          </Link>
        </CardHeader>
        <CardContent>
          {recentInvoices.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No hay facturas aún</p>
              <Link to="/dashboard/invoices/new">
                <button className="text-[#5B9BD5] hover:underline mt-2">Crear primera factura</button>
              </Link>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Folio</th>
                    <th>Cliente</th>
                    <th>Total</th>
                    <th>Estado</th>
                    <th>Fecha</th>
                  </tr>
                </thead>
                <tbody>
                  {recentInvoices.map((invoice) => (
                    <tr key={invoice.id}>
                      <td className="font-medium">{invoice.series}-{invoice.folio}</td>
                      <td>{invoice.client_name}</td>
                      <td>{formatCurrency(invoice.total)}</td>
                      <td>{getStatusBadge(invoice.status)}</td>
                      <td className="text-slate-500">
                        {formatDate(invoice.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Subscription status */}
      {company?.subscription_status === "trial" && (
        <Card className="cia-card border-amber-200 bg-amber-50">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h3 className="font-semibold text-amber-800">Periodo de Prueba</h3>
                <p className="text-amber-700 text-sm">
                  Estás usando CIA Factura℗ en modo prueba. Actualiza tu plan para funciones completas.
                </p>
              </div>
              <Link to="/dashboard/subscription">
                <button className="bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-lg" data-testid="upgrade-plan-btn">
                  Ver Planes
                </button>
              </Link>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CompanyDashboard;
